import * as Icons from "lucide-react";

/** Canonical processing lifecycle — one visual language everywhere. */
const STATE_STYLE = {
  done: { icon: "CheckCircle2", cls: "text-emerald-600", ring: "bg-emerald-500" },
  active: { icon: "Loader2", cls: "text-blue-600 animate-spin", ring: "bg-blue-500" },
  pending: { icon: "Circle", cls: "text-slate-300", ring: "bg-slate-200" },
  skipped: { icon: "MinusCircle", cls: "text-slate-400", ring: "bg-slate-300" },
  failed: { icon: "XCircle", cls: "text-rose-600", ring: "bg-rose-500" },
  blocked: { icon: "ShieldAlert", cls: "text-amber-600", ring: "bg-amber-500" },
};

const TERMINAL_TONE = {
  ready: "bg-emerald-100 text-emerald-700",
  partially_ready: "bg-amber-100 text-amber-700",
  quarantined: "bg-rose-100 text-rose-700",
  failed: "bg-rose-100 text-rose-700",
  processing: "bg-blue-100 text-blue-700",
};

const HEALTH_LABEL = {
  healthy: "Healthy", processing: "Processing", needs_review: "Needs Review",
  security_hold: "Security Hold", processing_failed: "Processing Failed",
};

export function ProcessingTimeline({ lifecycle, compact = true, testId = "processing-timeline" }) {
  if (!lifecycle) return null;
  const { timeline = [], terminal_state, terminal_label, ai_ready, health } = lifecycle;
  return (
    <div data-testid={testId} className="mt-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span data-testid={`${testId}-terminal`} className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${TERMINAL_TONE[terminal_state] || TERMINAL_TONE.processing}`}>
          {terminal_label || "Processing"}
        </span>
        {ai_ready && (
          <span data-testid={`${testId}-aiready`} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${ai_ready.ready ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"}`}>
            <Icons.Sparkles className="h-3 w-3" />AI {ai_ready.ready ? "Ready" : "Not Ready"}
          </span>
        )}
        {health && (
          <span data-testid={`${testId}-health`} className="text-[11px] text-slate-500">{HEALTH_LABEL[health] || health}</span>
        )}
      </div>
      <div className={`mt-2 ${compact ? "flex items-center gap-1 flex-wrap" : "space-y-1.5"}`}>
        {timeline.map((s) => {
          const st = STATE_STYLE[s.state] || STATE_STYLE.pending;
          const Ic = Icons[st.icon] || Icons.Circle;
          if (compact) {
            return (
              <span key={s.key} title={`${s.label}${s.note ? ` — ${s.note}` : ""}`} data-testid={`${testId}-stage-${s.key}`} data-state={s.state}
                className="inline-flex items-center">
                <Ic className={`h-3.5 w-3.5 ${st.cls}`} />
              </span>
            );
          }
          return (
            <div key={s.key} data-testid={`${testId}-stage-${s.key}`} data-state={s.state} className="flex items-center gap-2 text-xs">
              <Ic className={`h-3.5 w-3.5 ${st.cls}`} />
              <span className={s.state === "pending" ? "text-slate-400" : "text-slate-700"}>{s.label}</span>
              {s.note && <span className="text-slate-400">· {s.note}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ProcessingTimeline;
